import { request } from '@umijs/max';
import type { Talent, TalentListParams } from './talents';

/**
 * 过滤并序列化查询参数
 *
 * @param params - 原始查询对象
 */
const buildTalentParams = (
  params?: TalentListParams,
): Record<string, string | number> | undefined => {
  if (!params) {
    return undefined;
  }

  const result: Record<string, string | number> = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      if (value.length > 0) {
        result[key] = value.join(',');
      }
      return;
    }

    result[key] = value as string | number;
  });

  return Object.keys(result).length ? result : undefined;
};

/**
 * 人才收藏夹
 */
export interface TalentCollection {
  /** 收藏夹 UUID */
  uuid: string;
  /** 收藏夹名称 */
  name: string;
  /** 收藏夹描述 */
  description?: string;
  /** 上级收藏夹 UUID；为空表示根收藏夹 */
  parentUuid?: string | null;
  /** 子收藏夹（前端组装树时使用） */
  children?: TalentCollection[];
  /** 创建者 UUID */
  createdByUuid: string;
  /** 人才数量 */
  talentCount?: number;
  /** 创建时间 */
  createdAt: string;
  /** 更新时间 */
  updatedAt: string;
}

/**
 * 创建收藏夹参数
 */
export interface CreateTalentCollectionDto {
  /** 收藏夹名称 */
  name: string;
  /** 收藏夹描述 */
  description?: string;
  /** 上级收藏夹 UUID；为空表示根收藏夹 */
  parentUuid?: string | null;
}

/**
 * 更新收藏夹参数
 */
export interface UpdateTalentCollectionDto {
  /** 收藏夹名称 */
  name?: string;
  /** 收藏夹描述 */
  description?: string;
  /** 上级收藏夹 UUID；传 null 表示移动到根收藏夹 */
  parentUuid?: string | null;
}

export {
  buildCollectionTree as buildTalentCollectionTree,
  flattenCollectionTree as flattenTalentCollectionTree,
  getCollectionDescendantUuids as getTalentCollectionDescendantUuids,
} from '@/utils/collection-tree';

/**
 * 收藏夹人才列表响应
 */
export interface CollectionTalentsResponse {
  /** 数据列表 */
  data: Talent[];
  /** 总数 */
  total: number;
  /** 当前页码 */
  page: number;
  /** 单页数量 */
  limit: number;
}

/**
 * 批量添加人才到收藏夹响应
 */
export interface BatchAddTalentsResponse {
  /** 是否全部成功 */
  success: boolean;
  /** 提示信息 */
  message: string;
  /** 成功数量 */
  successCount: number;
  /** 失败数量 */
  failCount: number;
  /** 失败列表 */
  failList?: Array<{ talentUuid: string; reason: string }>;
}

/**
 * 人才收藏夹 API
 */
export const talentCollectionApi = {
  /**
   * 获取所有收藏夹列表
   */
  getCollections: () => {
    return request<TalentCollection[]>('/bff/luole-hunter/talent-collections');
  },

  /**
   * 获取收藏夹详情
   *
   * @param uuid - 收藏夹 UUID
   */
  getCollection: (uuid: string) => {
    return request<TalentCollection>(
      `/bff/luole-hunter/talent-collections/${uuid}`,
    );
  },

  /**
   * 创建收藏夹
   *
   * @param data - 创建收藏夹数据
   */
  createCollection: (data: CreateTalentCollectionDto) => {
    return request<TalentCollection>('/bff/luole-hunter/talent-collections', {
      method: 'POST',
      data,
    });
  },

  /**
   * 更新收藏夹
   *
   * @param uuid - 收藏夹 UUID
   * @param data - 更新收藏夹数据
   */
  updateCollection: (uuid: string, data: UpdateTalentCollectionDto) => {
    return request<TalentCollection>(
      `/bff/luole-hunter/talent-collections/${uuid}`,
      {
        method: 'PATCH',
        data,
      },
    );
  },

  /**
   * 删除收藏夹
   *
   * @param uuid - 收藏夹 UUID
   */
  deleteCollection: (uuid: string) => {
    return request<{ success: boolean; message: string }>(
      `/bff/luole-hunter/talent-collections/${uuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * 获取收藏夹中的人才列表
   *
   * @param collectionUuid - 收藏夹 UUID
   * @param params - 查询参数
   */
  getCollectionTalents: (collectionUuid: string, params?: TalentListParams) => {
    const queryParams = buildTalentParams(params);
    const options = queryParams ? { params: queryParams } : {};
    return request<CollectionTalentsResponse>(
      `/bff/luole-hunter/talent-collections/${collectionUuid}/talents`,
      options,
    );
  },

  /**
   * 获取收藏夹中可加入项目的私池人才（本人及组织管理范围）
   */
  getProjectCandidateTalents: (
    collectionUuid: string,
    params?: TalentListParams,
  ) => {
    return request<CollectionTalentsResponse>(
      `/bff/luole-hunter/talent-collections/${collectionUuid}/project-candidate-talents`,
      {
        params: buildTalentParams(params),
      },
    );
  },

  /**
   * 添加人才到收藏夹
   *
   * @param collectionUuid - 收藏夹 UUID
   * @param talentUuid - 人才 UUID
   */
  addTalentToCollection: (collectionUuid: string, talentUuid: string) => {
    return request<{ success: boolean; message: string }>(
      `/bff/luole-hunter/talent-collections/${collectionUuid}/talents`,
      {
        method: 'POST',
        data: { talentUuid },
      },
    );
  },

  /**
   * 从收藏夹移除人才
   *
   * @param collectionUuid - 收藏夹 UUID
   * @param talentUuid - 人才 UUID
   */
  removeTalentFromCollection: (collectionUuid: string, talentUuid: string) => {
    return request<{ success: boolean; message: string }>(
      `/bff/luole-hunter/talent-collections/${collectionUuid}/talents/${talentUuid}`,
      {
        method: 'DELETE',
      },
    );
  },

  /**
   * 批量添加人才到收藏夹
   *
   * @param collectionUuid - 收藏夹 UUID
   * @param talentUuids - 人才 UUID 列表
   */
  batchAddTalentsToCollection: (
    collectionUuid: string,
    talentUuids: string[],
  ) => {
    return request<BatchAddTalentsResponse>(
      `/bff/luole-hunter/talent-collections/${collectionUuid}/talents/batch`,
      {
        method: 'POST',
        data: { talentUuids },
      },
    );
  },
};
